import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Calendar, FileText, CreditCard, Users, Target, ClipboardList,
  Check, ArrowRight, Menu, X, Play, Star
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Feature data
const features = [
  {
    icon: FileText,
    title: 'Contract & Invoice Generation',
    description: 'Create professional contracts and invoices in seconds. Customize templates, track signatures, and manage payments all in one place.'
  },
  {
    icon: CreditCard,
    title: 'Online Payments',
    description: 'Accept payments through Stripe or Square. Clients can pay securely through their personalized portal with automatic recording.'
  },
  {
    icon: Users,
    title: 'Employee Management',
    description: 'Manage your team with role-based permissions. Assign staff to events, track availability, and control access to sensitive data.'
  },
  {
    icon: Target,
    title: 'Lead Management',
    description: 'Never lose a potential client. Track leads from first contact to booked event with customizable pipelines and follow-up reminders.'
  },
  {
    icon: Calendar,
    title: 'Booking Calendar',
    description: 'Visual calendar showing all your events. Manage availability, prevent double-bookings, and see your schedule at a glance.'
  },
  {
    icon: ClipboardList,
    title: 'Customizable Forms',
    description: 'Create planning forms for clients to fill out. Collect song requests, timeline details, and special requirements effortlessly.'
  }
];

// Screenshots/mockups for the app
const screenshots = [
  {
    title: 'Dashboard Overview',
    description: 'Get a quick snapshot of your business with upcoming events, revenue stats, and pending tasks.',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80'
  },
  {
    title: 'Client Portal',
    description: 'Give clients their own portal to view contracts, make payments, submit playlists, and communicate.',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80'
  },
  {
    title: 'Event Management',
    description: 'Everything you need for each event in one place - timeline, playlist, documents, and payments.',
    image: 'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=800&q=80'
  }
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pricing, setPricing] = useState({ monthly_amount: 49.99, yearly_amount: 539.89, trial_days: 14 });

  useEffect(() => {
    // Fetch dynamic pricing
    axios.get(`${API}/public/pricing`)
      .then(res => setPricing(res.data))
      .catch(() => {}); // Use defaults on error
  }, []);

  const yearlySavings = (pricing.monthly_amount * 12 - pricing.yearly_amount).toFixed(2);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-gray-100 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                DJ CRM
              </span>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-600 hover:text-gray-900 transition-colors">Features</a>
              <a href="#screenshots" className="text-gray-600 hover:text-gray-900 transition-colors">Screenshots</a>
              <a href="#pricing" className="text-gray-600 hover:text-gray-900 transition-colors">Pricing</a>
              <Button 
                variant="ghost" 
                onClick={() => navigate('/auth')}
                className="text-gray-600 hover:text-gray-900"
              >
                Login
              </Button>
              <Button 
                onClick={() => navigate('/auth?mode=register')}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:opacity-90"
              >
                Start Free Trial
              </Button>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-b border-gray-100 py-4 px-4">
            <div className="flex flex-col space-y-4">
              <a href="#features" className="text-gray-600 hover:text-gray-900" onClick={() => setMobileMenuOpen(false)}>Features</a>
              <a href="#screenshots" className="text-gray-600 hover:text-gray-900" onClick={() => setMobileMenuOpen(false)}>Screenshots</a>
              <a href="#pricing" className="text-gray-600 hover:text-gray-900" onClick={() => setMobileMenuOpen(false)}>Pricing</a>
              <Button variant="ghost" onClick={() => { navigate('/auth'); setMobileMenuOpen(false); }}>Login</Button>
              <Button onClick={() => { navigate('/auth?mode=register'); setMobileMenuOpen(false); }} className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                Start Free Trial
              </Button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 via-white to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-100 text-blue-700 text-sm font-medium mb-8">
              <Star className="w-4 h-4 mr-2 fill-current" />
              Trusted by 500+ DJ Companies
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
              The All-in-One CRM Built for{' '}
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                DJ Companies
              </span>
            </h1>
            
            <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
              Manage bookings, contracts, invoices, and client portals—all in one place. 
              Spend less time on admin and more time making music.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                onClick={() => navigate('/auth?mode=register')}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:opacity-90 text-lg px-8 py-6"
              >
                Start {pricing.trial_days}-Day Free Trial
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                onClick={() => document.getElementById('screenshots').scrollIntoView({ behavior: 'smooth' })}
                className="text-lg px-8 py-6 border-gray-300"
              >
                <Play className="mr-2 h-5 w-5" />
                See It In Action
              </Button>
            </div>
            
            <p className="mt-6 text-sm text-gray-500">
              No credit card required • {pricing.trial_days}-day free trial • Cancel anytime
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Everything You Need to Run Your DJ Business
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              From first inquiry to final payment, DJ CRM handles it all.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="p-6 rounded-2xl bg-gradient-to-br from-slate-50 to-white border border-gray-100 hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Screenshots Section */}
      <section id="screenshots" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              See DJ CRM in Action
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              A beautiful, intuitive interface designed specifically for DJ businesses.
            </p>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8">
            {screenshots.map((screenshot, index) => (
              <div key={index} className="group">
                <div className="relative overflow-hidden rounded-2xl shadow-lg mb-4">
                  <img 
                    src={screenshot.image} 
                    alt={screenshot.title}
                    className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-xl font-semibold text-white">{screenshot.title}</h3>
                  </div>
                </div>
                <p className="text-gray-600">{screenshot.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-gray-600">
              Start free, upgrade when you're ready. No hidden fees.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Monthly Plan */}
            <div className="rounded-2xl border border-gray-200 p-8 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Monthly</h3>
              <p className="text-gray-600 mb-6">Perfect for getting started</p>
              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-900">${pricing.monthly_amount.toFixed(2)}</span>
                <span className="text-gray-600">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                {['Unlimited bookings', 'Client portal', 'Contract & invoice generation', 'Online payments', 'Employee management', 'Email support'].map((feature, i) => (
                  <li key={i} className="flex items-center text-gray-600">
                    <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
              <Button 
                className="w-full bg-gray-900 text-white hover:bg-gray-800"
                onClick={() => navigate('/auth?mode=register')}
              >
                Start Free Trial
              </Button>
            </div>
            
            {/* Yearly Plan */}
            <div className="rounded-2xl border-2 border-blue-500 p-8 relative hover:shadow-lg transition-shadow bg-gradient-to-br from-blue-50 to-white">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-medium px-4 py-1 rounded-full">
                  Save ${yearlySavings}/year
                </span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Yearly</h3>
              <p className="text-gray-600 mb-6">Best value for growing businesses</p>
              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-900">${pricing.yearly_amount.toFixed(2)}</span>
                <span className="text-gray-600">/year</span>
                <p className="text-sm text-green-600 mt-1">That's ${(pricing.yearly_amount / 12).toFixed(2)}/month</p>
              </div>
              <ul className="space-y-3 mb-8">
                {['Everything in Monthly', '10% discount', 'Priority support', 'Early access to new features', 'Annual billing', 'Lock in your rate'].map((feature, i) => (
                  <li key={i} className="flex items-center text-gray-600">
                    <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
              <Button 
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:opacity-90"
                onClick={() => navigate('/auth?mode=register')}
              >
                Start Free Trial
              </Button>
            </div>
          </div>
          
          <p className="text-center mt-8 text-gray-500">
            All plans include a {pricing.trial_days}-day free trial. No credit card required.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Elevate Your DJ Business?
          </h2>
          <p className="text-xl text-blue-100 mb-10">
            Join hundreds of DJ companies already using DJ CRM to streamline their operations.
          </p>
          <Button 
            size="lg"
            onClick={() => navigate('/auth?mode=register')}
            className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-8 py-6"
          >
            Start Your Free {pricing.trial_days}-Day Trial
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-900 text-gray-400">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <span className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                DJ CRM
              </span>
              <p className="mt-4 text-sm">
                The all-in-one CRM built specifically for DJ companies and entertainment professionals.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#screenshots" className="hover:text-white transition-colors">Screenshots</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">About</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-sm text-center">
            <p>&copy; {new Date().getFullYear()} DJ CRM. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
